import React, { useState, useEffect } from 'react';
import ResponsiveContainer from '../../components/layout/ResponsiveContainer';
import SettingsCard from '../../components/ui/SettingsCard';
import { useApi } from '@/lib/useApi';

interface Homeowner {
  id: string;
  firstName: string;
  lastName: string;
  address: string;
  user: {
    email: string;
    phone: string;
  };
}

interface AdminEditHomeownerProfileProps {
  homeownerId: string;
  onNavigate: (screen: string) => void;
}

const AdminEditHomeownerProfile: React.FC<AdminEditHomeownerProfileProps> = ({
  homeownerId,
  onNavigate
}) => {
  const api = useApi();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [homeowner, setHomeowner] = useState<Homeowner | null>(null);

  // Homeowner information state
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [address, setAddress] = useState('');
  const [preferredContactMethod, setPreferredContactMethod] = useState('');
  const [timezone, setTimezone] = useState('');

  useEffect(() => {
    fetchHomeownerData();
  }, [homeownerId]);

  const fetchHomeownerData = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/api/admin/homeowners/${homeownerId}`);
      const data = response; // Backend returns homeowner data directly
      
      setHomeowner(data);

      // Set homeowner information
      setFirstName(data.firstName || '');
      setLastName(data.lastName || '');
      setEmail(data.user?.email || '');
      setPhone(data.user?.phone || '');
      setAddress(data.address || '');
      setPreferredContactMethod(data.preferredContactMethod || '');
      setTimezone(data.timezone || '');
    } catch (err) {
      console.error('Error fetching homeowner data:', err);
      setError('Failed to load homeowner data');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);

      // Update homeowner information
      if (homeowner?.id) {
        await api.put(`/api/admin/homeowners/${homeowner.id}`, {
          firstName,
          lastName,
          address,
          email,
          phone,
          preferredContactMethod,
          timezone
        });
      }

      setSuccess(true);
      setTimeout(() => {
        setSuccess(false);
        fetchHomeownerData(); // Refresh data
        // Notify parent component that data was updated
        onNavigate('admin-homeowners-list');
      }, 2000);
    } catch (err) {
      console.error('Error saving homeowner data:', err);
      setError('Failed to save homeowner changes');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteProfile = async () => {
    if (!confirm('Are you sure you want to delete this homeowner profile? This action cannot be undone and will delete all associated data.')) {
      return;
    }

    try {
      setSaving(true);
      setError(null);

      await api.delete(`/api/admin/homeowners/${homeownerId}`);

      setSuccess(true);
      setTimeout(() => {
        setSuccess(false);
        // Navigate back to homeowners list after successful deletion
        onNavigate('admin-homeowners-list');
      }, 2000);
    } catch (err) {
      console.error('Error deleting homeowner profile:', err);
      setError(err.message || 'Failed to delete homeowner profile');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '40px' }}>
        <div style={{ fontSize: 18, color: '#6b7280' }}>Loading homeowner data...</div>
      </div>
    );
  }

  if (!homeowner) {
    return (
      <div style={{ textAlign: 'center', padding: '40px' }}>
        <div style={{ fontSize: 18, color: '#ef4444' }}>Homeowner not found</div>
      </div>
    );
  }

  return (
    <ResponsiveContainer className="container">
      <div style={{ marginBottom: 24 }}>
        <button
          onClick={() => onNavigate('admin-homeowners-list')}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            backgroundColor: 'transparent',
            border: '1px solid #d1d5db',
            borderRadius: '6px',
            padding: '8px 16px',
            fontSize: '14px',
            color: '#374151',
            cursor: 'pointer',
            marginBottom: '16px'
          }}
        >
          ← Back to Homeowners
        </button>
        <h1 style={{ fontSize: 24, fontWeight: '600', color: '#111827', margin: 0 }}>
          Edit Profile - {homeowner.firstName} {homeowner.lastName}
        </h1>
        <p style={{ fontSize: 14, color: '#6b7280', margin: '8px 0 0 0' }}>
          Edit homeowner personal information and contact details
        </p>
      </div>

      {error && (
        <div style={{
          backgroundColor: '#fef2f2',
          border: '1px solid #fecaca',
          borderRadius: '8px',
          padding: '12px 16px',
          marginBottom: 24,
          color: '#dc2626'
        }}>
          <strong>Error:</strong> {error}
        </div>
      )}

      {success && (
        <div style={{
          backgroundColor: '#f0fdf4',
          border: '1px solid #bbf7d0',
          borderRadius: '8px',
          padding: '12px 16px',
          marginBottom: 24,
          color: '#16a34a'
        }}>
          <strong>Success:</strong> Changes saved successfully!
        </div>
      )}

      {/* Homeowner Information */}
      <SettingsCard title="Homeowner Information">
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 20 }}>
          <div>
            <label style={{ display: 'block', fontSize: 14, fontWeight: '600', color: '#374151', marginBottom: 6 }}>
              First Name
            </label>
            <input
              type="text"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '14px',
                backgroundColor: '#fff'
              }}
            />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 14, fontWeight: '600', color: '#374151', marginBottom: 6 }}>
              Last Name
            </label>
            <input
              type="text"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '14px',
                backgroundColor: '#fff'
              }}
            />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 14, fontWeight: '600', color: '#374151', marginBottom: 6 }}>
              Email
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '14px',
                backgroundColor: '#fff'
              }}
            />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 14, fontWeight: '600', color: '#374151', marginBottom: 6 }}>
              Phone
            </label>
            <input
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '14px',
                backgroundColor: '#fff'
              }}
            />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 14, fontWeight: '600', color: '#374151', marginBottom: 6 }}>
              Preferred Contact Method
            </label>
            <select
              value={preferredContactMethod}
              onChange={(e) => setPreferredContactMethod(e.target.value)}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '14px',
                backgroundColor: '#fff'
              }}
            >
              <option value="">Select...</option>
              <option value="EMAIL">Email</option>
              <option value="PHONE">Phone</option>
              <option value="SMS">SMS</option>
            </select>
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 14, fontWeight: '600', color: '#374151', marginBottom: 6 }}>
              Timezone
            </label>
            <input
              type="text"
              value={timezone}
              onChange={(e) => setTimezone(e.target.value)}
              placeholder="e.g., America/New_York"
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '14px',
                backgroundColor: '#fff'
              }}
            />
          </div>
        </div>
      </SettingsCard>

      {/* Save and Delete Buttons */}
      <div style={{ marginTop: 24, textAlign: 'center', display: 'flex', gap: 16, justifyContent: 'center' }}>
        <button
          onClick={handleSave}
          disabled={saving}
          style={{
            backgroundColor: '#10b981',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            padding: '12px 24px',
            fontSize: '16px',
            fontWeight: '600',
            cursor: saving ? 'not-allowed' : 'pointer',
            opacity: saving ? 0.7 : 1,
            minWidth: '200px'
          }}
        >
          {saving ? 'Saving...' : 'Save Profile Changes'}
        </button>
        <button
          onClick={handleDeleteProfile}
          disabled={saving}
          style={{
            backgroundColor: '#dc2626',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            padding: '12px 24px',
            fontSize: '16px',
            fontWeight: '600',
            cursor: saving ? 'not-allowed' : 'pointer',
            opacity: saving ? 0.7 : 1,
            minWidth: '200px'
          }}
        >
          {saving ? 'Deleting...' : 'Delete Profile'}
        </button>
      </div>
    </ResponsiveContainer>
  );
};

export default AdminEditHomeownerProfile;
